// Чистый модуль импорта сценариев из JSON: парсинг, валидация, нормализация.
// Без React и сетевых зависимостей — тестируется изолированно.
import type { ScenarioSavePayload } from 'services/scenariosApi';

/** Сырой объект сценария из импортируемого JSON (до валидации). */
export interface RawScenario {
  role_id?: unknown;
  title?: unknown;
  description?: unknown;
  steps?: unknown;
  action?: unknown;
  result?: unknown;
  instruction?: unknown;
  system_prompt?: unknown;
  model?: unknown;
  min_files?: unknown;
  max_files?: unknown;
  ordering?: unknown;
}

export interface ValidationResult {
  ok: boolean;
  errors: string[];
}

/** Парсит строку JSON и проверяет, что корень — непустой массив объектов. */
export function parseScenariosJson(raw: string): {
  items: RawScenario[] | null;
  error: string | null;
} {
  if (!raw || !raw.trim()) {
    return { items: null, error: 'Пустой ввод: вставьте JSON или загрузите файл' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'неизвестная ошибка';
    return { items: null, error: `Невалидный JSON: ${msg}` };
  }
  if (!Array.isArray(parsed)) {
    return { items: null, error: 'Корень JSON должен быть массивом сценариев' };
  }
  return { items: parsed as RawScenario[], error: null };
}

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
const isNumberOrUndefined = (v: unknown): boolean =>
  v === undefined || v === null || typeof v === 'number';

/**
 * Валидирует один сценарий.
 * knownRoleIds — известные роли (для проверки role_id). Если набор пуст
 * (роли ещё не загружены), проверка существования роли пропускается.
 */
export function validateItem(item: RawScenario, knownRoleIds: Set<string>): ValidationResult {
  const errors: string[] = [];

  if (!isNonEmptyString(item.title)) {
    errors.push('Поле title обязательно и должно быть непустой строкой');
  }
  if (!isNonEmptyString(item.role_id)) {
    errors.push('Поле role_id обязательно и должно быть непустой строкой');
  } else if (knownRoleIds.size > 0 && !knownRoleIds.has(item.role_id)) {
    errors.push(`Роль не найдена: role_id "${item.role_id}" отсутствует в системе`);
  }

  if (item.steps !== undefined && !Array.isArray(item.steps)) {
    errors.push('Поле steps должно быть массивом строк');
  }
  if (item.action !== undefined && typeof item.action !== 'string') {
    errors.push('Поле action должно быть строкой');
  }
  if (!isNumberOrUndefined(item.min_files)) {
    errors.push('Поле min_files должно быть числом');
  }
  if (!isNumberOrUndefined(item.max_files)) {
    errors.push('Поле max_files должно быть числом');
  }
  if (!isNumberOrUndefined(item.ordering)) {
    errors.push('Поле ordering должно быть числом');
  }

  return { ok: errors.length === 0, errors };
}

const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);

/** Суффикс, добавляемый к названию каждого импортируемого сценария. */
export const TEST_TITLE_SUFFIX = ' (тестовый)';

/** Добавляет суффикс «(тестовый)» к названию (идемпотентно, пустые не трогает). */
export function applyTestSuffix(title: string): string {
  const trimmed = title.trim();
  if (!trimmed) return title;
  if (trimmed.endsWith(TEST_TITLE_SUFFIX.trim())) return title;
  return `${title}${TEST_TITLE_SUFFIX}`;
}

/** Нормализует сырой объект в payload для createScenario. steps приоритетнее action. */
export function toPayload(item: RawScenario): ScenarioSavePayload {
  const action = Array.isArray(item.steps)
    ? item.steps.map((s) => String(s)).join('\n')
    : str(item.action);

  const payload: ScenarioSavePayload = {
    role_id: String(item.role_id ?? ''),
    title: applyTestSuffix(String(item.title ?? '')),
  };

  const description = str(item.description);
  if (description !== undefined) payload.description = description;
  if (action !== undefined) payload.action = action;
  const result = str(item.result);
  if (result !== undefined) payload.result = result;
  const instruction = str(item.instruction);
  if (instruction !== undefined) payload.instruction = instruction;
  const systemPrompt = str(item.system_prompt);
  if (systemPrompt !== undefined) payload.system_prompt = systemPrompt;

  // model: строка или явный null
  if (item.model === null || typeof item.model === 'string') {
    payload.model = item.model as string | null;
  }

  const minFiles = num(item.min_files);
  if (minFiles !== undefined) payload.min_files = minFiles;
  const maxFiles = num(item.max_files);
  if (maxFiles !== undefined) payload.max_files = maxFiles;
  const ordering = num(item.ordering);
  if (ordering !== undefined) payload.ordering = ordering;

  return payload;
}

/** Краткое имя сценария для логов/превью. */
export function scenarioTitle(item: RawScenario, index: number): string {
  return isNonEmptyString(item.title) ? item.title : `Без названия #${index + 1}`;
}

/**
 * Строит готовый промпт для любого ИИ (ChatGPT, Claude и т.п.),
 * чтобы он сгенерировал корректный JSON под наш импорт.
 * Список ролей подставляется реальный — чтобы ИИ использовал валидные role_id.
 */
export function buildAiPrompt(roles: Array<{ id: string; name: string }>): string {
  const rolesBlock = roles.length
    ? roles.map((r) => `- "${r.name}" → role_id: "${r.id}"`).join('\n')
    : '- (роли не загружены — подставьте role_id вручную из админки)';

  return `Ты помогаешь подготовить JSON для импорта сценариев в систему «Контекст».

Сгенерируй МАССИВ JSON-объектов. Каждый объект — один сценарий со полями:
- role_id (строка, ОБЯЗАТЕЛЬНО) — UUID роли из списка ниже
- title (строка, ОБЯЗАТЕЛЬНО) — название сценария
- description (строка) — краткое описание
- steps (массив строк) — шаги выполнения
- result (строка) — ожидаемый результат
- instruction (строка) — инструкция пользователю
- system_prompt (строка) — системный промпт для ИИ
- model (строка или null) — оставь null
- min_files, max_files (числа) — мин/макс число файлов
- ordering (число) — порядок сортировки

Доступные роли (используй ТОЛЬКО эти role_id):
${rolesBlock}

Требования к ответу:
1. Верни ТОЛЬКО валидный JSON-массив, без markdown-обёртки и пояснений.
2. role_id бери строго из списка выше.
3. Не добавляй слово «тестовый» в title — система добавит его сама.

Тема сценариев: <ОПИШИ ЗДЕСЬ, какие сценарии нужны>`;
}

/** Пример массива для кнопки «Скачать шаблон». */
export const SCENARIOS_IMPORT_TEMPLATE = JSON.stringify(
  [
    {
      role_id: 'ЗАМЕНИТЕ-НА-UUID-РОЛИ',
      title: 'Анализ договора',
      description: 'Проверка договора на риски',
      steps: ['Загрузить документ', 'Сверить условия с шаблоном', 'Выделить расхождения'],
      result: 'Список рисков и рекомендаций',
      instruction: 'Приложите PDF или DOCX договора',
      system_prompt: 'Ты — юрист, анализируешь договоры на риски.',
      model: null,
      min_files: 1,
      max_files: 3,
      ordering: 0,
    },
    {
      role_id: 'ЗАМЕНИТЕ-НА-UUID-РОЛИ',
      title: 'Сводка по отчёту',
      description: 'Краткое резюме квартального отчёта',
      action: 'Прочитать отчёт\nВыделить ключевые метрики\nСоставить сводку',
      result: 'Резюме на одну страницу',
      min_files: 1,
      max_files: 1,
      ordering: 1,
    },
  ],
  null,
  2
);
